
import { getProductModel } from '@/api';
import { Component, Vue, Prop, Watch } from 'vue-property-decorator';
import { ValidationProvider, extend } from 'vee-validate';
import { required } from 'vee-validate/dist/rules';
import Icon from '@/components/UI/Icon.vue';
import { JobComplianceProductLookupData } from '@/models/JobComplianceTypes';
import { getModelFromString, getPrefixFromString } from '@/helpers/booking';

extend('required', {
  ...required,
  message: 'This field is required',
});

@Component({
  components: {
    Icon,
    ValidationProvider,
  },
})
export default class LookupProductModel extends Vue {
  @Prop({ type: String, required: false, default: '0' }) public name: string;
  @Prop({ type: String, required: false, default: '' }) public variant: string;
  @Prop({ type: String, required: false, default: '' }) public model: string;
  @Prop({ type: Number, required: false, default: '' }) public model_id: number | null;

  public loading = false;
  public productValue = '';
  public selectItems: string[] = [];
  public selectItemsData: JobComplianceProductLookupData[] = [];
  public showOptions = false;
  public valueChosen = '';
  public arrowIdx = -1;

  @Watch('valueChosen', { deep: true })
  private watchValueChosen() {
    const pref = getPrefixFromString(this.model);
    const chosenItem = this.selectItemsData.find(x => x.name === this.valueChosen);
    if (chosenItem) {
      chosenItem.name = pref + chosenItem.name;
      this.$emit('changed', chosenItem);
    }
  }

  private applyValueFromParent(item: string): void {
    this.productValue = item;
    this.showOptions = false;
    this.valueChosen = item;
    this.$forceUpdate();
  }

  public async handleInput(evt: string): Promise<void> {
    if (evt || evt === '') {
      this.productValue = evt;
    }
    if (this.productValue.length >= 3) {
      this.loading = true;
      await getProductModel(this.productValue)
        .then((data: JobComplianceProductLookupData[]) => {
          this.selectItems = data.map(x => x.name);
          this.selectItemsData = data;
        })
        .catch(e => console.log(e))
        .finally(() => {
          this.loading = false;
        });
    }
  }

  public inputKeyPressed(evt: KeyboardEvent): void {
    // Open dropdown on Focus
    if (!this.showOptions) {
      this.showOptions = true;
      this.arrowIdx = -1;
    }
    // Close dropdown when "Escape" pressed
    if (evt.code === 'Escape') {
      this.showOptions = false;
      this.arrowIdx = -1;
    }
    // ArrowDown / ArrowUp pressed
    if (evt.code === 'ArrowDown' && this.arrowIdx < this.selectItems.length - 1) {
      this.arrowIdx += 1;
      this.fixScrolling();
    }
    if (evt.code === 'ArrowUp' && this.arrowIdx >= 1) {
      this.arrowIdx -= 1;
      this.fixScrolling();
    }
    // Enter pressed
    if (evt.code === 'Enter' && this.arrowIdx >= 0) {
      evt.preventDefault();
      this.valueChosen = this.selectItems[this.arrowIdx];
      this.showOptions = false;
      this.productValue = this.selectItems[this.arrowIdx];
      this.arrowIdx = -1;
    }
  }

  private fixScrolling() {
    const options = this.$refs.options as HTMLFormElement;
    const scrollContainer = this.$refs.scrollContainer as HTMLFormElement;
    if (options && scrollContainer) {
      const liH = options[this.arrowIdx].clientHeight;
      scrollContainer.scrollTop = liH * this.arrowIdx;
    }
  }

  public clearInput(): void {
    this.productValue = '';
    this.showOptions = false;
    this.valueChosen = '';
    this.arrowIdx = -1;
    this.selectItems = [];
  }

  public inputFocused(): void {
    this.showOptions = true;
    if (this.productValue.length >= 3) {
      this.handleInput(this.productValue);
    }
  }

  public inputBlured(): void {
    setTimeout(() => {
      if (this.valueChosen !== this.productValue) {
        this.productValue = '';
      }
    }, 300);
    setTimeout(() => {
      this.showOptions = false;
    }, 500);
  }

  public handleItemClick(item: string): void {
    this.valueChosen = item;
    this.showOptions = false;
    this.productValue = item;
    this.arrowIdx = -1;
  }

  public clickedOutside(): void {
    this.showOptions = false;
  }

  @Watch('model', { immediate: true })
  private watchPropTypeCode() {
    if (this.model) {
      if (this.model.includes(': ')) {
        this.productValue = getModelFromString(this.model);
      } else {
        this.productValue = this.model;
      }
    }
  }
}
